import React, { useState } from "react";
import QrScanButton from "../components/QrScanButton";
import { useScannerInput } from "../lib/scannerInput";
import api, { fmtErr } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { ScanLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function SareeGodownIn() {
  const [code, setCode] = useState("");
  const [token, setToken] = useState(null);
  const [sanction, setSanction] = useState({ amount: "", description: "" });
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const scanner = useScannerInput((v) => { setCode(v); search(v); });
  const search = async (val) => {
    const c = typeof val === "string" ? val : code;
    if (!c.trim()) return;
    setLoading(true);
    setDone(false);
    try {
      const r = await api.get(`/sarees/${encodeURIComponent(c.trim())}`);
      setToken(r.data);
    } catch (e) {
      setToken(null);
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  const verify = async () => {
    setLoading(true);
    try {
      await api.post(`/sarees/${token.token_no}/verify-in`, { sanction_amount: Number(sanction.amount) || 0, sanction_description: sanction.description });
      search();
      setDone(true);
      toast.success("Godown In verified");
    } catch (e) { toast.error(fmtErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="godown-in-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Sarees — Godown In</h1>
      <p className="text-sm text-slate-500 mb-5">Scan the access card QR or enter the token number, verify details and record godown-in.</p>

      <div className="scan-row flex gap-2 mb-6">
        <Input ref={scanner.ref} autoFocus data-testid="godownin-code-input" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="Scan QRCode / enter Token No" className="h-12 font-mono" onKeyDown={scanner.onKeyDown} />
        <QrScanButton onScan={(t) => { setCode(t); search(t); scanner.refocus(); }} label="Camera" />
        <Button data-testid="godownin-search-btn" onClick={search} disabled={loading} className="h-12 px-6 bg-maroon-700 hover:bg-maroon-800 text-white">
          <ScanLine className="w-5 h-5 mr-2" /> Search Verify Details
        </Button>
      </div>

      {token && (
        <Card className="card-gold-top shadow-sm" data-testid="godownin-details">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg text-maroon-900">Token Details</CardTitle>
            <StatusBadge status={token.status} saree />
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              {token.photo ? (
                <img src={token.photo} alt="saree" className="w-28 h-32 object-cover rounded-md border-2 border-gold-400 shrink-0" data-testid="godownin-saree-photo" />
              ) : (
                <div className="w-28 h-32 rounded-md border-2 border-dashed border-gold-400 flex items-center justify-center text-[10px] text-slate-400 shrink-0" data-testid="godownin-no-photo">No photo</div>
              )}
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm flex-1">
              <div><p className="text-xs text-gold-700 font-bold uppercase">Token No</p><p className="font-mono">{token.token_no}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Date</p><p>{new Date(token.created_at).toLocaleString("en-IN")}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Category</p><p>{token.category}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Saree Type</p><p>{token.saree_type_name}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Rate</p><p>₹{token.rate}</p></div>
              <div><p className="text-xs text-gold-700 font-bold uppercase">Phone</p><p>{token.phone || "-"}</p></div>
              {token.name && <div><p className="text-xs text-gold-700 font-bold uppercase">Name</p><p>{token.name}</p></div>}
              {token.description && <div className="col-span-2"><p className="text-xs text-gold-700 font-bold uppercase">Description</p><p>{token.description}</p></div>}
              </div>
            </div>
            {token.status === "ISSUED" && !done && (
              <div className="border-t border-[#E5D9C5] pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Sanction Amount (₹)</Label><Input type="number" min={0} value={sanction.amount} onChange={(e) => setSanction({ ...sanction, amount: e.target.value })} data-testid="godownin-sanction-amount" /></div>
                <div><Label>Sanction Description</Label><Textarea rows={1} value={sanction.description} onChange={(e) => setSanction({ ...sanction, description: e.target.value })} data-testid="godownin-sanction-desc" /></div>
                <Button onClick={verify} disabled={loading} data-testid="godownin-verify-btn"
                  className="sm:col-span-2 h-12 bg-green-700 hover:bg-green-800 text-white font-heading tracking-widest">
                  VERIFY — GODOWN IN
                </Button>
              </div>
            )}
            {done && (
              <div className="rounded-lg bg-emerald-100 border border-emerald-300 text-emerald-900 p-4 text-center font-heading font-bold flex items-center justify-center gap-2" data-testid="godownin-done">
                <CheckCircle2 className="w-5 h-5" /> GODOWN IN VERIFIED
              </div>
            )}
            {token.status === "GODOWN_IN" && !done && token.verified_by && (
              <p className="text-sm text-slate-500">Already verified into godown by {token.verified_by} on {new Date(token.verified_at).toLocaleString("en-IN")}.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
